import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudDownload as DownloadIcon,
  FolderOpen as FolderOpenIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon
} from '@mui/icons-material';
import { TableVirtuoso } from 'react-virtuoso';
import LogInventory from './inventory';
import { DEEP_GREEN, MUTED, INK, NEUTRAL_SURFACE, L2_ACCENT, L2_BADGE_BG, INCIDENT_ACCENT, WARN_ORANGE } from '../colorTokens';
import { MultiCheckSelect, ALL_FILTER, filterAllows } from '../MultiCheckSelect';

// Mirrors app.js's traversal pruning — shouldIncludeFile (from inventory.js)
// still makes the final per-file include/exclude call.
const IGNORED_DIRECTORIES = new Set([
  '.git', '.gradle', '.idea', '.mypy_cache', '.pytest_cache', '.tox',
  '.venv', 'bin', 'build', 'coverage', 'dist', 'node_modules', 'out',
  'target', 'test', 'tests', '__tests__', 'vendor', 'venv'
]);

// Files per worker message -- keeps structured-clone payloads and in-flight memory bounded on large repos.
const WORKER_CHUNK_SIZE = 200;

const SUCCESS_GREEN = '#2e7d32'; // darkened to meet WCAG AA 4.5:1 contrast on white
const WARN_ORANGE_BG = '#fbe9dd';
const ERROR_BG = '#fdecea';

const LEVEL_FILTER_OPTIONS = [
  { value: 'Error', label: 'Error' },
  { value: 'Warn', label: 'Warning' },
  { value: 'Info', label: 'Info' },
  { value: 'Debug', label: 'Debug' }
];
const PII_FILTER_OPTIONS = [
  { value: 'true', label: 'true' },
  { value: 'false', label: 'false' }
];

// Package-detail popup toggle options -- 'All' plus one entry per Level, plus
// the special PII-risk criterion (not a Level value).
const PACKAGE_DIALOG_FILTERS = [
  { value: 'All', label: 'All' },
  { value: 'Error', label: 'Error' },
  { value: 'Warn', label: 'Warning' },
  { value: 'Info', label: 'Info' },
  { value: 'Debug', label: 'Debug' },
  { value: 'PIIRisk', label: 'PII risk' }
];

const LEVEL_STYLES = {
  Error: { color: INCIDENT_ACCENT, bg: ERROR_BG },
  Warn: { color: WARN_ORANGE, bg: WARN_ORANGE_BG },
  Info: { color: L2_ACCENT, bg: L2_BADGE_BG },
  Debug: { color: MUTED, bg: NEUTRAL_SURFACE }
};

// Wraps every case-insensitive occurrence of `term` in `value` with a
// highlighted <mark> -- used for the Inventory details search. The row
// currently navigated to (isActive) gets a distinct, stronger color so it's
// visually different from the other matched rows.
const highlightMatches = (value, term, isActive) => {
  const text = String(value ?? '');
  if (!term) return text;
  const lower = text.toLowerCase();
  const needle = term.toLowerCase();
  const firstIndex = lower.indexOf(needle);
  if (firstIndex === -1) return text;
  const markStyle = isActive
    ? { backgroundColor: '#ffa726', color: '#1A1A1A', padding: 0 }
    : { backgroundColor: '#fff176', color: 'inherit', padding: 0 };
  const parts = [];
  let start = 0;
  let index = firstIndex;
  while (index !== -1) {
    if (index > start) parts.push(text.slice(start, index));
    parts.push(
      <mark key={index} style={markStyle}>
        {text.slice(index, index + needle.length)}
      </mark>
    );
    start = index + needle.length;
    index = lower.indexOf(needle, start);
  }
  if (start < text.length) parts.push(text.slice(start));
  return parts;
};

async function collectFiles(directoryHandle, prefix, callbacks) {
  const { onDirectory, onSkippedDirectory } = callbacks;
  const files = [];
  const displayPath = prefix || directoryHandle.name;
  onDirectory(displayPath);
  for await (const [name, handle] of directoryHandle.entries()) {
    const relativePath = prefix ? `${prefix}/${name}` : name;
    if (handle.kind === 'directory') {
      if (IGNORED_DIRECTORIES.has(name)) {
        onSkippedDirectory(relativePath);
      } else {
        // Avoid spreading into push() args -- large subtrees can exceed the engine's argument limit.
        for (const file of await collectFiles(handle, relativePath, callbacks)) files.push(file);
      }
    } else if (LogInventory.shouldIncludeFile(relativePath)) {
      try {
        const file = await handle.getFile();
        files.push({ path: relativePath, content: await file.text() });
      } catch {
        // Match the original: skip unreadable source files rather than failing the scan.
      }
    }
  }
  return files;
}

const PiiDonut = ({ title, rows }) => {
  const total = rows.length;
  const trueCount = rows.filter((row) => row.PIIRisk === 'true').length;
  const falseCount = total - trueCount;
  const size = 96;
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const trueLength = total > 0 ? (trueCount / total) * circumference : 0;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: '12px', textAlign: 'center', minWidth: 150 }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: MUTED, mb: 1 }}>{title}</Typography>
      <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto' }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8ea" strokeWidth={strokeWidth} />
          {total > 0 && (
            <>
              <circle
                cx={size / 2} cy={size / 2} r={radius} fill="none"
                stroke={INCIDENT_ACCENT} strokeWidth={strokeWidth}
                strokeDasharray={`${trueLength} ${circumference - trueLength}`}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
              <circle
                cx={size / 2} cy={size / 2} r={radius} fill="none"
                stroke={SUCCESS_GREEN} strokeWidth={strokeWidth}
                strokeDasharray={`${circumference - trueLength} ${trueLength}`}
                strokeDashoffset={-trueLength}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
              />
            </>
          )}
        </svg>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: INK }}>{total}</Typography>
          <Typography sx={{ fontSize: '0.625rem', color: MUTED }}>messages</Typography>
        </Box>
      </Box>
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography sx={{ fontSize: '0.6875rem', color: INCIDENT_ACCENT, fontWeight: 600 }}>PIIRisk true: {trueCount}</Typography>
        <Typography sx={{ fontSize: '0.6875rem', color: SUCCESS_GREEN, fontWeight: 600 }}>PIIRisk false: {falseCount}</Typography>
      </Box>
    </Paper>
  );
};

const dirCountTileSx = (accent) => ({
  appearance: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  p: 1.25,
  minWidth: 170,
  borderRadius: '10px',
  border: '1px solid rgba(0,0,0,0.12)',
  borderTop: `3px solid ${accent}`,
  bgcolor: '#fff',
  cursor: 'pointer',
  textAlign: 'left',
  font: 'inherit',
  '&:hover': { boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }
});

// Fills its parent <TableCell> so the package drill-down actions are real,
// keyboard-focusable/activatable buttons rather than onClick handlers on a <td>.
const PackageCellButton = ({ onClick, align = 'left', ariaLabel, sx, children }) => (
  <ButtonBase
    onClick={onClick}
    aria-label={ariaLabel}
    sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      px: 2,
      py: 0.75,
      font: 'inherit',
      color: 'inherit',
      textDecoration: 'underline',
      textDecorationColor: 'transparent',
      '&:hover': { textDecorationColor: 'currentColor' },
      '&.Mui-focusVisible': { outlineOffset: '-2px', outline: `2px solid ${L2_ACCENT}` },
      ...sx
    }}
  >
    {children}
  </ButtonBase>
);

const LoggingInventoryPage = () => {
  const [status, setStatus] = useState({ message: 'Choose a directory to begin.', kind: 'normal' });
  const [busy, setBusy] = useState(false);
  const [processedDirs, setProcessedDirs] = useState([]);
  const [skippedDirs, setSkippedDirs] = useState([]);
  const [processedDialogOpen, setProcessedDialogOpen] = useState(false);
  const [skippedDialogOpen, setSkippedDialogOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [hasResults, setHasResults] = useState(false);
  const [draftLevelFilter, setDraftLevelFilter] = useState([ALL_FILTER]);
  const [draftPiiFilter, setDraftPiiFilter] = useState([ALL_FILTER]);
  const [levelFilter, setLevelFilter] = useState([ALL_FILTER]);
  const [piiFilter, setPiiFilter] = useState([ALL_FILTER]);
  const [packageDialogOpen, setPackageDialogOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageDialogFilter, setPackageDialogFilter] = useState('All');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const workerRef = useRef(null);
  const detailsVirtuosoRef = useRef(null);

  useEffect(() => () => {
    if (workerRef.current) workerRef.current.terminate();
  }, []);

  const scanWithWorker = useCallback((files, onProgress) => new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./worker.js', import.meta.url));
    workerRef.current = worker;
    const rows = [];
    // Posting the whole tree as one structured-clone message can block the UI and spike
    // memory on large repos -- feed the worker bounded chunks and merge results as they land.
    const totalChunks = Math.max(1, Math.ceil(files.length / WORKER_CHUNK_SIZE));
    let chunkIndex = 0;

    const cleanup = () => {
      worker.terminate();
      workerRef.current = null;
    };
    const sendNextChunk = () => {
      const start = chunkIndex * WORKER_CHUNK_SIZE;
      worker.postMessage(files.slice(start, start + WORKER_CHUNK_SIZE));
    };

    worker.addEventListener('message', (event) => {
      if (event.data.error) {
        cleanup();
        reject(new Error(event.data.error));
        return;
      }
      for (const row of event.data.rows) rows.push(row);
      chunkIndex += 1;
      onProgress?.(chunkIndex, totalChunks);
      if (chunkIndex >= totalChunks) {
        cleanup();
        // Each chunk arrives pre-sorted, but chunk arrival order doesn't follow File/Line -- re-sort the merge.
        rows.sort((left, right) => left.File.toLowerCase().localeCompare(right.File.toLowerCase()) || Number(left.Line) - Number(right.Line));
        resolve({ rows });
      } else {
        sendNextChunk();
      }
    });
    worker.addEventListener('error', (event) => {
      cleanup();
      reject(new Error(event.message || 'Logging inventory scan failed'));
    });
    sendNextChunk();
  }), []);

  const handleChooseDirectory = useCallback(async () => {
    if (!('showDirectoryPicker' in window)) {
      setStatus({ message: 'Directory selection requires a current Chromium-based browser such as Edge or Chrome.', kind: 'error' });
      return;
    }
    try {
      const directoryHandle = await window.showDirectoryPicker({ mode: 'read' });
      setBusy(true);
      setProcessedDirs([]);
      setSkippedDirs([]);
      setStatus({ message: `Reading directory: ${directoryHandle.name}`, kind: 'working' });
      // Accumulate in plain arrays (O(1) push) and flush to state on a timer
      // instead of copying the whole list -- O(D) spread -- on every single
      // directory, which made large-repo traversal O(D^2) re-renders.
      const processedDirsBuffer = [];
      const skippedDirsBuffer = [];
      let lastProcessedFlush = 0;
      const flushProcessedDirs = (force) => {
        const now = Date.now();
        if (!force && now - lastProcessedFlush < 150) return;
        lastProcessedFlush = now;
        setProcessedDirs([...processedDirsBuffer]);
      };
      const files = await collectFiles(directoryHandle, '', {
        onDirectory: (dir) => {
          processedDirsBuffer.push(dir);
          flushProcessedDirs(false);
          setStatus({ message: `Reading directory: ${dir}`, kind: 'working' });
        },
        onSkippedDirectory: (dir) => {
          skippedDirsBuffer.push(dir);
          setSkippedDirs([...skippedDirsBuffer]);
        }
      });
      flushProcessedDirs(true);
      setStatus({ message: `Analyzing ${files.length} source files...`, kind: 'working' });
      const result = await scanWithWorker(files, (chunksDone, totalChunks) => {
        if (totalChunks > 1) {
          setStatus({ message: `Analyzing source files... (${chunksDone}/${totalChunks})`, kind: 'working' });
        }
      });
      const withIds = result.rows.map((row, index) => ({ ...row, rowId: `row-${index}` }));
      setRows(withIds);
      setDraftLevelFilter([ALL_FILTER]);
      setDraftPiiFilter([ALL_FILTER]);
      setLevelFilter([ALL_FILTER]);
      setPiiFilter([ALL_FILTER]);
      setSearchInput('');
      setSearchTerm('');
      setSearchMatchIndex(0);
      setHasResults(true);
      setStatus({ message: `Completed ${directoryHandle.name}: ${withIds.length} logging calls found.`, kind: 'success' });
    } catch (error) {
      if (error.name !== 'AbortError') {
        setStatus({ message: `Unable to scan directory: ${error.message}`, kind: 'error' });
      }
    } finally {
      setBusy(false);
    }
  }, [scanWithWorker]);

  const handleDownloadCsv = useCallback(() => {
    const blob = new Blob([LogInventory.createCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = LogInventory.CSV_NAME;
    link.click();
    URL.revokeObjectURL(url);
  }, [rows]);

  const filteredRows = useMemo(
    () => rows.filter((row) => filterAllows(levelFilter, row.Level) && filterAllows(piiFilter, row.PIIRisk)),
    [rows, levelFilter, piiFilter]
  );

  // Search only ever runs over filteredRows -- whatever Level/PIIRisk filters
  // are currently applied -- so it naturally only searches what's displayed.
  const matchingRowIndexes = useMemo(() => {
    if (!searchTerm) return [];
    const needle = searchTerm.toLowerCase();
    const indexes = [];
    filteredRows.forEach((row, index) => {
      const haystack = ['File', 'Line', 'Level', 'Message', 'DataAppended', 'PIIRisk', 'PIIRiskSource']
        .map((field) => String(row[field] ?? ''))
        .join('\u0000')
        .toLowerCase();
      if (haystack.includes(needle)) indexes.push(index);
    });
    return indexes;
  }, [filteredRows, searchTerm]);

  const handleSearch = useCallback(() => {
    setSearchTerm(searchInput.trim());
    setSearchMatchIndex(0);
  }, [searchInput]);

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setSearchTerm('');
    setSearchMatchIndex(0);
  }, []);

  // Jumping straight to a row far from the current viewport is estimated
  // using an average row height until virtuoso actually measures the rows in
  // between (variable-height wrapped Message/File text makes that estimate
  // unreliable) -- scrolling again one frame later, once real sizes are
  // known, corrects the landing spot instead of stopping short/past it.
  const scrollToMatch = useCallback((position) => {
    const rowIndex = matchingRowIndexes[position];
    if (rowIndex === undefined) return;
    detailsVirtuosoRef.current?.scrollToIndex({ index: rowIndex, align: 'center', behavior: 'auto' });
    requestAnimationFrame(() => {
      detailsVirtuosoRef.current?.scrollToIndex({ index: rowIndex, align: 'center', behavior: 'auto' });
    });
  }, [matchingRowIndexes]);

  const goToRelativeMatch = useCallback((delta) => {
    if (matchingRowIndexes.length === 0) return;
    const next = (searchMatchIndex + delta + matchingRowIndexes.length) % matchingRowIndexes.length;
    setSearchMatchIndex(next);
    scrollToMatch(next);
  }, [matchingRowIndexes, searchMatchIndex, scrollToMatch]);

  // Jump to the first match only when a new search is actually submitted
  // (searchTerm changes), not on every filteredRows/matchingRowIndexes
  // recompute -- e.g. an Apply-filters click shouldn't yank the scroll
  // position out from under an already-running search.
  useEffect(() => {
    if (searchTerm && matchingRowIndexes.length > 0) {
      scrollToMatch(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handleApplyFilters = useCallback(() => {
    setLevelFilter(draftLevelFilter);
    setPiiFilter(draftPiiFilter);
    setSearchMatchIndex(0);
  }, [draftLevelFilter, draftPiiFilter]);

  const summary = useMemo(() => LogInventory.summarizeRows(filteredRows), [filteredRows]);
  const packageNames = useMemo(
    () => Object.keys(summary).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
    [summary]
  );

  const totals = useMemo(() => {
    const acc = Object.fromEntries(LogInventory.LEVEL_NAMES.map((level) => [level, 0]));
    let piiTotal = 0;
    packageNames.forEach((name) => {
      LogInventory.LEVEL_NAMES.forEach((level) => { acc[level] += summary[name][level] || 0; });
      piiTotal += summary[name].piiRiskMessages || 0;
    });
    return { ...acc, piiTotal };
  }, [summary, packageNames]);

  const openPackageDialog = useCallback((packageName, filterPreset) => {
    setSelectedPackage(packageName);
    setPackageDialogFilter(filterPreset);
    setPackageDialogOpen(true);
  }, []);

  // Always the full unfiltered scan for this package -- per spec, the popup
  // ignores whatever Level/PIIRisk filters are currently applied above.
  const packageDialogRows = useMemo(
    () => (selectedPackage ? rows.filter((row) => row.Package === selectedPackage) : []),
    [rows, selectedPackage]
  );
  const packageDialogFilteredRows = useMemo(() => {
    if (packageDialogFilter === 'All') return packageDialogRows;
    if (packageDialogFilter === 'PIIRisk') return packageDialogRows.filter((row) => row.PIIRisk === 'true');
    return packageDialogRows.filter((row) => row.Level === packageDialogFilter);
  }, [packageDialogRows, packageDialogFilter]);

  // Per-toggle counts for the popup -- always against the full (unfiltered)
  // packageDialogRows, so a toggle's count never depends on which toggle is
  // currently selected.
  const packageDialogCounts = useMemo(() => {
    const counts = { All: packageDialogRows.length };
    LogInventory.LEVEL_NAMES.forEach((level) => {
      counts[level] = packageDialogRows.filter((row) => row.Level === level).length;
    });
    counts.PIIRisk = packageDialogRows.filter((row) => row.PIIRisk === 'true').length;
    return counts;
  }, [packageDialogRows]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: INK }}>Logging Inventory</Typography>
      <Typography sx={{ color: MUTED, fontSize: '0.875rem', mt: 0.5, mb: 2 }}>
        Analyze logging calls in a local source directory without uploading its files.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<FolderOpenIcon />}
          disabled={busy}
          onClick={handleChooseDirectory}
          sx={{ textTransform: 'none', bgcolor: DEEP_GREEN, '&:hover': { bgcolor: DEEP_GREEN, filter: 'brightness(0.92)' } }}
        >
          Choose directory to scan
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          disabled={!hasResults || rows.length === 0}
          onClick={handleDownloadCsv}
          sx={{ textTransform: 'none', color: DEEP_GREEN, borderColor: DEEP_GREEN }}
        >
          Download CSV
        </Button>
      </Box>
      <Typography
        sx={{
          mt: 1.5,
          fontSize: '0.8125rem',
          fontWeight: status.kind === 'error' ? 700 : 500,
          color: status.kind === 'error' ? INCIDENT_ACCENT : status.kind === 'success' ? SUCCESS_GREEN : MUTED
        }}
      >
        {status.message}
      </Typography>

      {(busy || processedDirs.length > 0) && (
        <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', maxWidth: 640 }}>
          <Box
            component="button"
            type="button"
            onClick={() => setProcessedDialogOpen(true)}
            sx={dirCountTileSx(DEEP_GREEN)}
          >
            {busy && <CircularProgress size={16} />}
            <Box>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: MUTED, letterSpacing: '0.02em' }}>
                Directories Processed
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: DEEP_GREEN }}>
                {processedDirs.length}
              </Typography>
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => setSkippedDialogOpen(true)}
            sx={dirCountTileSx(MUTED)}
          >
            <Box>
              <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: MUTED, letterSpacing: '0.02em' }}>
                Directories Skipped
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1.25rem', color: MUTED }}>
                {skippedDirs.length}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Dialog open={processedDialogOpen} onClose={() => setProcessedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Directories Processed ({processedDirs.length})
          <IconButton size="small" onClick={() => setProcessedDialogOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {processedDirs.length === 0 ? (
            <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>No directories processed yet.</Typography>
          ) : processedDirs.map((dir, index) => (
            <Typography key={`${dir}-${index}`} sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: INK, py: 0.25 }}>
              {index + 1}. {dir}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessedDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={skippedDialogOpen} onClose={() => setSkippedDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Directories Skipped ({skippedDirs.length})
          <IconButton size="small" onClick={() => setSkippedDialogOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {skippedDirs.length === 0 ? (
            <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>No directories skipped.</Typography>
          ) : skippedDirs.map((dir, index) => (
            <Typography key={`${dir}-${index}`} sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: INK, py: 0.25 }}>
              {index + 1}. {dir}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkippedDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={packageDialogOpen} onClose={() => setPackageDialogOpen(false)} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {selectedPackage} ({packageDialogFilteredRows.length} of {packageDialogRows.length})
          <IconButton size="small" onClick={() => setPackageDialogOpen(false)} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={packageDialogFilter}
            onChange={(_, next) => { if (next) setPackageDialogFilter(next); }}
            sx={{ mb: 2, flexWrap: 'wrap' }}
            aria-label="Filter messages shown"
          >
            {PACKAGE_DIALOG_FILTERS.map((option) => {
              const count = packageDialogCounts[option.value] ?? 0;
              return (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  disabled={count === 0}
                  sx={{ textTransform: 'none' }}
                >
                  {option.label} ({count})
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>

          <TableVirtuoso
            style={{ height: 480 }}
            data={packageDialogFilteredRows}
            computeItemKey={(_index, row) => row.rowId}
            components={{
              Scroller: React.forwardRef((props, ref) => (
                <TableContainer component={Paper} variant="outlined" ref={ref} {...props} sx={{ borderRadius: '10px' }} />
              )),
              Table: (props) => <Table {...props} size="small" stickyHeader sx={{ tableLayout: 'fixed' }} />,
              TableHead,
              TableRow: ({ item, ...props }) => <TableRow {...props} />,
              TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />)
            }}
            fixedHeaderContent={() => (
              <TableRow sx={{ bgcolor: '#fff' }}>
                <TableCell sx={{ width: '40ch', minWidth: '40ch', maxWidth: '40ch' }}>File</TableCell>
                <TableCell>Line</TableCell>
                <TableCell>Level</TableCell>
                <TableCell sx={{ width: '40ch', minWidth: '40ch', maxWidth: '40ch' }}>Message</TableCell>
                <TableCell sx={{ width: '40ch', minWidth: '40ch', maxWidth: '40ch' }}>DataAppended</TableCell>
                <TableCell>PIIRisk</TableCell>
                <TableCell>PIIRiskSource</TableCell>
              </TableRow>
            )}
            itemContent={(_index, row) => {
              const levelStyle = LEVEL_STYLES[row.Level] || { color: MUTED, bg: NEUTRAL_SURFACE };
              const cellBg = row.PIIRisk === 'true' ? { bgcolor: ERROR_BG } : undefined;
              return (
                <>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', width: '40ch', minWidth: '40ch', maxWidth: '40ch', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...cellBg }}>
                    {row.File}
                  </TableCell>
                  <TableCell sx={cellBg}>{row.Line}</TableCell>
                  <TableCell sx={cellBg}>
                    <Box
                      component="span"
                      sx={{
                        px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.6875rem', fontWeight: 700,
                        color: levelStyle.color, bgcolor: levelStyle.bg
                      }}
                    >
                      {row.Level}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', width: '40ch', minWidth: '40ch', maxWidth: '40ch', ...cellBg }}>
                    {row.Message}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', width: '40ch', minWidth: '40ch', maxWidth: '40ch', ...cellBg }}>
                    {row.DataAppended}
                  </TableCell>
                  <TableCell sx={{ color: row.PIIRisk === 'true' ? INCIDENT_ACCENT : SUCCESS_GREEN, fontWeight: 600, ...cellBg }}>
                    {row.PIIRisk}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', ...cellBg }}>{row.PIIRiskSource}</TableCell>
                </>
              );
            }}
          />
          {packageDialogFilteredRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No messages match this filter.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPackageDialogOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {!hasResults && !busy && (
        <Alert severity="info" sx={{ mt: 3, maxWidth: 640 }}>
          Your files stay local — this only reads source files into browser memory and never uploads or modifies them.
          Directory selection requires a Chromium-based browser (Edge or Chrome).
        </Alert>
      )}

      {hasResults && (
        <>
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1 }}>Message overview</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              <PiiDonut title="All Messages" rows={filteredRows} />
              {LogInventory.LEVEL_NAMES.map((level) => (
                <PiiDonut key={level} title={level} rows={filteredRows.filter((row) => row.Level === level)} />
              ))}
            </Box>
          </Box>

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'flex-end', gap: 1.5, flexWrap: 'wrap' }}>
            <MultiCheckSelect
              label="Level"
              value={draftLevelFilter}
              onChange={setDraftLevelFilter}
              options={LEVEL_FILTER_OPTIONS}
              minWidth={180}
              listLabels
            />
            <MultiCheckSelect
              label="PIIRisk"
              value={draftPiiFilter}
              onChange={setDraftPiiFilter}
              options={PII_FILTER_OPTIONS}
              minWidth={160}
            />
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              sx={{ textTransform: 'none', bgcolor: DEEP_GREEN, '&:hover': { bgcolor: DEEP_GREEN, filter: 'brightness(0.92)' } }}
            >
              Apply
            </Button>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1 }}>Messages logged per package</Typography>
            {/* ~5 data rows tall (row height varies slightly with content) before scrolling kicks in. */}
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, borderRadius: '12px' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Package</TableCell>
                    {LogInventory.LEVEL_NAMES.map((level) => (
                      <TableCell key={level} align="right">{level}</TableCell>
                    ))}
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">PIIRisk</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {packageNames.map((packageName) => {
                    const packageSummary = summary[packageName];
                    const packageTotal = LogInventory.LEVEL_NAMES.reduce((sum, level) => sum + (packageSummary[level] || 0), 0);
                    const hasPiiRisk = (packageSummary.piiRiskMessages || 0) > 0;
                    // Backgrounds set on each cell, not the row -- the theme's
                    // ".MuiTableRow-root:nth-of-type(even)" zebra stripe (theme.js)
                    // is more specific than a row-level sx class and silently wins
                    // on even rows, so a row-level bgcolor only "took" on odd rows.
                    const cellBg = hasPiiRisk ? { bgcolor: ERROR_BG } : undefined;
                    return (
                      <TableRow key={packageName} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', p: 0, ...cellBg }}>
                          <PackageCellButton
                            onClick={() => openPackageDialog(packageName, 'All')}
                            ariaLabel={`View all logging calls for package ${packageName}`}
                          >
                            {packageName}
                          </PackageCellButton>
                        </TableCell>
                        {LogInventory.LEVEL_NAMES.map((level) => (
                          <TableCell key={level} align="right" sx={{ p: 0, ...cellBg }}>
                            <PackageCellButton
                              align="right"
                              onClick={() => openPackageDialog(packageName, level)}
                              ariaLabel={`View ${level} logging calls for package ${packageName}`}
                            >
                              {packageSummary[level] || 0}
                            </PackageCellButton>
                          </TableCell>
                        ))}
                        <TableCell align="right" sx={{ p: 0, ...cellBg }}>
                          <PackageCellButton
                            align="right"
                            onClick={() => openPackageDialog(packageName, 'All')}
                            ariaLabel={`View all logging calls for package ${packageName}`}
                            sx={{ fontWeight: 700 }}
                          >
                            {packageTotal}
                          </PackageCellButton>
                        </TableCell>
                        <TableCell align="right" sx={{ p: 0, ...cellBg }}>
                          <PackageCellButton
                            align="right"
                            onClick={() => openPackageDialog(packageName, 'PIIRisk')}
                            ariaLabel={`View PII-risk logging calls for package ${packageName}`}
                          >
                            {packageSummary.piiRiskMessages || 0}
                          </PackageCellButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow sx={{ bgcolor: NEUTRAL_SURFACE }}>
                    <TableCell sx={{ fontWeight: 700 }}>ALL PACKAGES</TableCell>
                    {LogInventory.LEVEL_NAMES.map((level) => (
                      <TableCell key={level} align="right" sx={{ fontWeight: 700 }}>{totals[level]}</TableCell>
                    ))}
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{filteredRows.length}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{totals.piiTotal}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', mb: 1 }}>Inventory details</Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
              <TextField
                size="small"
                placeholder="Search for anything"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Enter') handleSearch(); }}
                sx={{ width: '150ch' }}
              />
              <Button
                variant="contained"
                onClick={searchTerm ? handleClearSearch : handleSearch}
                sx={{ textTransform: 'none', bgcolor: DEEP_GREEN, '&:hover': { bgcolor: DEEP_GREEN, filter: 'brightness(0.92)' } }}
              >
                {searchTerm ? 'Clear' : 'Search'}
              </Button>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <IconButton
                  size="small"
                  onClick={() => goToRelativeMatch(-1)}
                  disabled={matchingRowIndexes.length === 0}
                  aria-label="Previous match"
                  sx={{ p: 0.25 }}
                >
                  <ArrowUpIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => goToRelativeMatch(1)}
                  disabled={matchingRowIndexes.length === 0}
                  aria-label="Next match"
                  sx={{ p: 0.25 }}
                >
                  <ArrowDownIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            {searchTerm && (
              <Typography sx={{ mb: 1.5, fontSize: '0.8125rem', color: MUTED }}>
                {matchingRowIndexes.length > 0
                  ? `Match ${searchMatchIndex + 1} of ${matchingRowIndexes.length}`
                  : 'No matches found'}
              </Typography>
            )}

            {/* Virtualized (react-virtuoso) -- only rows near the viewport are ever
                mounted in the DOM, so the full, uncapped result set (however large)
                stays reachable by scrolling without overflowing React's dev-mode
                Fast Refresh fiber walker (scheduleFibersWithFamiliesRecursively). */}
            <TableVirtuoso
              ref={detailsVirtuosoRef}
              style={{ height: 660 }}
              data={filteredRows}
              computeItemKey={(_index, row) => row.rowId}
              components={{
                Scroller: React.forwardRef((props, ref) => (
                  <TableContainer component={Paper} variant="outlined" ref={ref} {...props} sx={{ borderRadius: '12px' }} />
                )),
                Table: (props) => <Table {...props} size="small" stickyHeader sx={{ tableLayout: 'fixed' }} />,
                TableHead,
                TableRow: ({ item, ...props }) => <TableRow hover {...props} />,
                TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />)
              }}
              fixedHeaderContent={() => (
                <TableRow sx={{ bgcolor: '#fff' }}>
                  <TableCell sx={{ width: '50ch', minWidth: '50ch', maxWidth: '50ch' }}>File</TableCell>
                  <TableCell>Line</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell sx={{ width: '50ch', minWidth: '50ch', maxWidth: '50ch' }}>Message</TableCell>
                  <TableCell>DataAppended</TableCell>
                  <TableCell>PIIRisk</TableCell>
                  <TableCell>PIIRiskSource</TableCell>
                </TableRow>
              )}
              itemContent={(_index, row) => {
                const levelStyle = LEVEL_STYLES[row.Level] || { color: MUTED, bg: NEUTRAL_SURFACE };
                const isActiveMatch = matchingRowIndexes.length > 0 && matchingRowIndexes[searchMatchIndex] === _index;
                return (
                  <>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', width: '50ch', minWidth: '50ch', maxWidth: '50ch', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{highlightMatches(row.File, searchTerm, isActiveMatch)}</TableCell>
                    <TableCell>{highlightMatches(row.Line, searchTerm, isActiveMatch)}</TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        sx={{
                          px: 1, py: 0.25, borderRadius: '4px', fontSize: '0.6875rem', fontWeight: 700,
                          color: levelStyle.color, bgcolor: levelStyle.bg
                        }}
                      >
                        {highlightMatches(row.Level, searchTerm, isActiveMatch)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', width: '50ch', minWidth: '50ch', maxWidth: '50ch' }}>
                      {highlightMatches(row.Message, searchTerm, isActiveMatch)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{highlightMatches(row.DataAppended, searchTerm, isActiveMatch)}</TableCell>
                    <TableCell sx={{ color: row.PIIRisk === 'true' ? INCIDENT_ACCENT : SUCCESS_GREEN, fontWeight: 600 }}>
                      {highlightMatches(row.PIIRisk, searchTerm, isActiveMatch)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{highlightMatches(row.PIIRiskSource, searchTerm, isActiveMatch)}</TableCell>
                  </>
                );
              }}
            />
            {filteredRows.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No logging calls match these filters.
              </Typography>
            )}
          </Box>
          <Divider sx={{ mt: 3 }} />
        </>
      )}
    </Box>
  );
};

export default LoggingInventoryPage;
